import { zodResolver } from "@hookform/resolvers/zod";
import { useUpdateCustomerProfileMutation } from "@stepapp/core";
import {
  Block,
  BottomSheet,
  Button,
  Divider,
  Input,
  SvgIcon,
  Touchable,
  Typography,
  palette,
  spacing,
  useHeaderHeight,
} from "@stepapp/ui";
import React, { useCallback } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { KeyboardAvoidingView, Platform, StyleSheet } from "react-native";
import { useAppDispatch } from "../../src/hooks";
import { useEmailSchema } from "../../src/schemas/registerSchema";
import { editUserData } from "../../src/store/user";

type FormValues = {
  email: string;
};

type Props = {
  setVisible: (v: boolean) => void;
  visible: boolean;
};

const ChangeEmailModal = ({ setVisible, visible }: Props) => {
  const { t } = useTranslation();
  const headerHeight = useHeaderHeight();
  const appDispatch = useAppDispatch();
  const emailSchema = useEmailSchema();

  const [updateCustomerProfile, { loading }] = useUpdateCustomerProfileMutation();

  const { handleSubmit, setValue, watch, formState, reset } = useForm({
    mode: "onChange",
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: "",
    },
  });

  const email = watch("email");

  const handleClose = useCallback(() => {
    setVisible(false);
    reset();
  }, [setVisible, reset]);

  const handleChangeEmail = useCallback(
    (value: string) => {
      setValue("email", value, { shouldValidate: true });
    },
    [setValue],
  );

  const onSubmit = useCallback(
    async (values: FormValues) => {
      const { data } = await updateCustomerProfile({
        variables: { data: { email: values.email } },
      });

      if (data?.updateCustomerProfile) {
        appDispatch(
          editUserData({
            email: values.email,
          }),
        );
        handleClose();
        return;
      }
      alert(t("alerts.cantUpdate"));
    },
    [updateCustomerProfile, handleClose, appDispatch, t],
  );

  return (
    <BottomSheet contentStyle={styles.container} toggle={handleClose} visible={visible}>
      <Block>
        <Touchable onPress={handleClose} style={styles.iconContainer}>
          <SvgIcon name="chevron-left" size={24} />
        </Touchable>

        <Typography variant="newH2" style={styles.additionalPadding}>
          {t("screens.settings.updateEmailAddress")}
        </Typography>

        <Block style={styles.inputContainer}>
          <Input
            placeholder={t("screens.settings.email")}
            value={email}
            onChangeText={handleChangeEmail}
          />
        </Block>
      </Block>

      <KeyboardAvoidingView
        behavior={Platform.select({ default: "padding", android: undefined })}
        keyboardVerticalOffset={headerHeight + 60}
      >
        <Divider spaceless color={palette.secondary.grey} />
        <Button
          label={t("common.save")}
          onPress={handleSubmit(onSubmit)}
          type="primary"
          style={styles.saveButton}
          disabled={!formState.isValid || loading}
          loading={loading}
        />
      </KeyboardAvoidingView>
    </BottomSheet>
  );
};

export default React.memo(ChangeEmailModal);

const styles = StyleSheet.create({
  additionalPadding: {
    paddingHorizontal: spacing.unit * 2.5,
  },
  inputContainer: {
    paddingTop: spacing.unit * 3,
    paddingHorizontal: spacing.unit * 2.5,
  },
  saveButton: {
    paddingBottom: spacing.unit * 2,
    paddingHorizontal: spacing.unit * 2.5,
  },
  container: {
    height: spacing.deviceHeight * 0.8,
    paddingVertical: spacing.unit * 2.5,
    justifyContent: "space-between",
  },
  iconContainer: {
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: palette.secondary.grey,
    borderRadius: spacing.radius * 2,
    height: spacing.unit * 4.5,
    width: spacing.unit * 4.5,
    marginHorizontal: spacing.unit * 2.5,
    marginBottom: spacing.unit * 2,
  },
});
